import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import UrgencyBar from "../components/UrgencyBar";

function FAQItem({ item, isOpen, onToggle }) {
  const slug = item.q.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "");
  const btnId = `faq-btn-${slug}`;
  const panelId = `faq-panel-${slug}`;
  return (
    <div className="border-b border-zinc-800">
      <button
        id={btnId}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="w-full flex items-center justify-between py-5 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] rounded-sm"
      >
        <span className="text-white text-sm font-medium pr-4">{item.q}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" aria-hidden="true" />
        )}
      </button>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={btnId}>
          <p className="text-zinc-400 text-sm leading-relaxed pb-5 pr-8">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => {
    setOpenFaq((prev) => (prev === idx ? null : idx));
  };

  const FAQ_ITEMS = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
    { q: t('faq.q7'), a: t('faq.a7') },
    { q: t('faq.q8'), a: t('faq.a8') },
    { q: t('faq.q9'), a: t('faq.a9') },
    { q: t('faq.q10'), a: t('faq.a10') },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t("faq.seo_title", { defaultValue: "FAQ | Frequently Asked Questions" })}
        description={t("faq.seo_desc", { defaultValue: "Find answers to common questions about Max OS Cloud, AI agents, pricing, integrations, security, and getting started." })}
        url="/faq"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_ITEMS.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          },
        ]}
      />
      <Header />
      <UrgencyBar />

      <main id="main-content">
        <section className="relative overflow-hidden border-b border-zinc-900" aria-labelledby="faq-hero-heading">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" aria-hidden="true" />
          <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">
              // {t('faq.section_label').replace('// ', '').toUpperCase()}
            </div>
            <h1 id="faq-hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl uppercase leading-[0.95] max-w-4xl mx-auto">
              {t('faq.title_1')}
              <br />
              <span className="text-amber-500">{t('faq.title_2')}</span>
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-lg text-zinc-400 leading-relaxed">
              {t('faq.subtitle')}
            </p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16" aria-labelledby="faq-list-heading">
          <h2 id="faq-list-heading" className="sr-only">{t('faq.title')}</h2>
          <div className="border-t border-zinc-800">
            {FAQ_ITEMS.map((item, idx) => (
              <FAQItem
                key={idx}
                item={item}
                isOpen={openFaq === idx}
                onToggle={() => toggleFaq(idx)}
              />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
